import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEffect, useMemo, useState } from 'react';

export interface TemplateAccessItem {
  projectRef: string;
  entitlementId: string;
  capabilityPackId: string;
  capabilityPackVersion: string;
  templateRef: string;
  operation: string;
}

export interface TemplateAccessResponse {
  enforced: boolean;
  adminBypass: boolean;
  items: TemplateAccessItem[];
}

export function useTemplateAccess() {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [access, setAccess] = useState<TemplateAccessResponse>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [baseUrl, credentials] = await Promise.all([
          discoveryApi.getBaseUrl('arta-governance'),
          identityApi.getCredentials(),
        ]);
        const response = await fetch(`${baseUrl}/template-access`, {
          headers: credentials.token
            ? { Authorization: `Bearer ${credentials.token}` }
            : undefined,
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(
            body.error ?? `Template access failed with HTTP ${response.status}`,
          );
        }
        if (!cancelled) setAccess(await response.json());
      } catch (cause) {
        if (!cancelled)
          setError(cause instanceof Error ? cause : new Error(String(cause)));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, identityApi]);

  const allowedTemplateRefs = useMemo(
    () => new Set(access?.items.map(item => item.templateRef.toLowerCase())),
    [access],
  );
  return { access, allowedTemplateRefs, error };
}

export function templateEntityRef(entity: {
  kind?: string;
  metadata: { name: string; namespace?: string };
}): string {
  return `${entity.kind ?? 'template'}:${
    entity.metadata.namespace ?? 'default'
  }/${entity.metadata.name}`.toLowerCase();
}
